import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { MedicalRecordsSearchState, MedicalRecordsSubmissionState } from '../hooks/useMedicalRecords';
import type { MedicalRecordsEncounterView, MedicalRecordsSort } from '../data/medicalRecordsViews';
import { MedicalRecordsSortControl } from './MedicalRecordsSortControl';

interface MedicalRecordsSearchPanelProps {
  onDismissFeedback: () => void;
  sort: MedicalRecordsSort;
  onSortChange: (sort: MedicalRecordsSort) => void;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  onSubmit: () => void;
  onClear: () => void;
  canClear: boolean;
  state: MedicalRecordsSearchState;
  onRequest: (encounter: MedicalRecordsEncounterView) => void;
  activeRequestedEncounterIds: ReadonlySet<string>;
  isRequesting: boolean;
  submissionState: MedicalRecordsSubmissionState;
  onPreviousPage: () => void;
  onNextPage: () => void;
}

const ERROR_MESSAGES: Record<'denied' | 'unavailable' | 'general', string> = {
  denied: 'You do not have permission to search encounters for this center.',
  unavailable: 'Medical Records search is temporarily unavailable. Please try again shortly.',
  general: 'Unable to search encounters. Please try again.',
};

const SUBMISSION_ERROR_MESSAGES: Record<'denied' | 'unavailable' | 'general' | 'conflict', string> = {
  conflict: 'A previous request prevents a new request for this encounter. Check My Requests. If the failed request cannot be retried, contact support to close its previous attempt.',
  denied: 'You do not have permission to request documents for this center.',
  unavailable: 'Medical Records requests are temporarily unavailable. Please try again shortly.',
  general: 'Unable to request documents. Please try again.',
};

interface MedicalRecordsSearchResultRowProps {
  item: MedicalRecordsEncounterView;
  alreadyRequested: boolean;
  isRequesting: boolean;
  submissionState: MedicalRecordsSubmissionState;
  onRequest: (encounter: MedicalRecordsEncounterView) => void;
}

const MedicalRecordsSearchResultRow: React.FC<MedicalRecordsSearchResultRowProps> = ({
  item,
  alreadyRequested,
  isRequesting,
  submissionState,
  onRequest,
}) => (
  <TableRow data-testid="medical-records-search-row">
    <TableCell>{item.patientName}</TableCell>
    <TableCell>{item.mrn}</TableCell>
    <TableCell>{item.accountNumber}</TableCell>
    <TableCell>{item.serviceDate ? new Date(item.serviceDate).toLocaleDateString() : ''}</TableCell>
    <TableCell>{item.addedDate ? new Date(item.addedDate).toLocaleDateString() : ''}</TableCell>
    <TableCell>
      <Button
        size="small"
        startIcon={<DescriptionIcon />}
        disabled={alreadyRequested || isRequesting}
        onClick={() => onRequest(item)}
        data-testid="medical-records-download-button"
        aria-label={alreadyRequested ? `Already requested for ${item.patientName}` : `Request documents for ${item.patientName}`}
      >
        {alreadyRequested ? 'Already Requested' : 'Request Documents'}
      </Button>
      {alreadyRequested && <Box sx={{ mt: 0.5, fontSize: '0.75rem' }}>Check My Requests. A previous request is active or awaiting close-out.</Box>}
    </TableCell>
  </TableRow>
);

interface SearchControlsProps {
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  onSubmit: () => void;
  onClear: () => void;
  canClear: boolean;
}

const SearchControls: React.FC<SearchControlsProps> = ({
  searchTerm, onSearchTermChange, onSubmit, onClear, canClear,
}) => (
  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
    <TextField
      label="Search encounters"
      placeholder="Patient name, MRN or account"
      size="small"
      value={searchTerm}
      onChange={(event) => onSearchTermChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onSubmit();
      }}
      inputProps={{ 'data-testid': 'medical-records-search-input' }}
      sx={{ flex: 1, minWidth: 240 }}
    />
    <Button variant="contained" onClick={onSubmit} data-testid="medical-records-search-submit">
      Search
    </Button>
    <Button variant="outlined" onClick={onClear} disabled={!canClear} data-testid="medical-records-search-clear">
      Clear
    </Button>
  </Box>
);

const SubmissionFeedback: React.FC<{ state: MedicalRecordsSubmissionState; onDismiss: () => void }> = ({ state, onDismiss }) => {
  if (state.status === 'accepted') {
    return <Alert severity="success" onClose={onDismiss} data-testid="medical-records-submit-success" sx={{ mb: 2 }}>Request added to My Requests.</Alert>;
  }
  if (state.status === 'error') {
    return <Alert severity={state.kind === 'conflict' ? 'warning' : 'error'} onClose={onDismiss}
      data-testid="medical-records-submit-error" sx={{ mb: 2 }}>{SUBMISSION_ERROR_MESSAGES[state.kind]}</Alert>;
  }
  return null;
};

interface SearchStateContentProps {
  sort: MedicalRecordsSort;
  onSortChange: (sort: MedicalRecordsSort) => void;
  state: MedicalRecordsSearchState;
  onRequest: (encounter: MedicalRecordsEncounterView) => void;
  activeRequestedEncounterIds: ReadonlySet<string>;
  isRequesting: boolean;
  submissionState: MedicalRecordsSubmissionState;
  onPreviousPage: () => void;
  onNextPage: () => void;
}

const SearchStateContent: React.FC<SearchStateContentProps> = ({
  state, onRequest, activeRequestedEncounterIds, isRequesting, submissionState, onPreviousPage, onNextPage, sort, onSortChange,
}) => {
  if (state.status === 'loading') {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress data-testid="medical-records-search-loading" /></Box>;
  }
  if (state.status === 'error') {
    return <Alert severity="error" data-testid="medical-records-search-error">{ERROR_MESSAGES[state.kind]}</Alert>;
  }
  if (state.status === 'empty') {
    return <Alert severity="info" data-testid="medical-records-search-empty">No encounters found for this search.</Alert>;
  }
  if (state.status !== 'data') return null;

  return (
    <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
      <Table size="small" data-testid="medical-records-search-results" sx={{ minWidth: 720 }}>
        <TableHead>
          <TableRow>
            {[['patient', 'Patient'], ['mrn', 'MRN'], ['account', 'Account'],
              ['serviceDate', 'Service Date'], ['created', 'Added date']].map(([field, label]) => (
              <TableCell key={field} sortDirection={sort.field === field && (sort.descending ? 'desc' : 'asc')}>
                <MedicalRecordsSortControl field={field} value={sort} onChange={onSortChange}>{label}</MedicalRecordsSortControl>
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {state.items.map((item) => (
            <MedicalRecordsSearchResultRow
              key={item.encounterId}
              item={item}
              alreadyRequested={activeRequestedEncounterIds.has(item.encounterId)}
              isRequesting={isRequesting}
              submissionState={submissionState}
              onRequest={onRequest}
            />
          ))}
        </TableBody>
      </Table>
      {(state.hasPrevious || state.hasNext) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
          <Tooltip title="Previous search results"><span><IconButton aria-label="Previous search results" disabled={!state.hasPrevious} onClick={onPreviousPage}><ChevronLeftIcon /></IconButton></span></Tooltip>
          <Tooltip title="Next search results"><span><IconButton aria-label="Next search results" disabled={!state.hasNext} onClick={onNextPage}><ChevronRightIcon /></IconButton></span></Tooltip>
        </Box>
      )}
    </TableContainer>
  );
};

/**
 * Encounter search identifying context (patient, MRN, account, or visit) plus one Request
 * Documents action per result. Submit-driven — a partial term does not fire a request on every
 * keystroke.
 */
const MedicalRecordsSearchPanel: React.FC<MedicalRecordsSearchPanelProps> = ({
  onDismissFeedback,
  sort, onSortChange,
  searchTerm,
  onSearchTermChange,
  onSubmit,
  onClear,
  canClear,
  state,
  onRequest,
  activeRequestedEncounterIds,
  isRequesting,
  submissionState,
  onPreviousPage,
  onNextPage,
}) => (
  <Box>
    <SearchControls {...{ searchTerm, onSearchTermChange, onSubmit, onClear, canClear }} />
    <SubmissionFeedback state={submissionState} onDismiss={onDismissFeedback} />
    <SearchStateContent {...{
      state, onRequest, activeRequestedEncounterIds, isRequesting, submissionState, onPreviousPage, onNextPage, sort, onSortChange,
    }} />
  </Box>
);

export default MedicalRecordsSearchPanel;
